import { type StaffMenu, staffMenuSchema } from "@luckymenu/core";
import type { QueryClient } from "@tanstack/react-query";
import type { KeyValueStorage } from "../adapters";

/**
 * The menu kept on the device (OP-17, P3-T27): the last `listMenu` answer
 * of each restaurant is written to the platform's storage, and read back
 * into the query cache before the first fetch, so the Menu tab and the item
 * picker open at once, and work without a connection. The server's answer
 * replaces it as soon as it arrives.
 */

const PREFIX = "luckymenu.menu.";
const VERSION = 1;

export function menuStorageKey(restaurantId: string): string {
	return PREFIX + restaurantId;
}

export function encodeMenu(menu: StaffMenu, savedAt: number): string {
	return JSON.stringify({ v: VERSION, savedAt, menu });
}

/** The stored menu, or null when missing, from another version, or not a menu. */
export function decodeMenu(raw: string | null): { menu: StaffMenu; savedAt: number } | null {
	if (!raw) return null;
	try {
		const value = JSON.parse(raw) as { v?: unknown; savedAt?: unknown; menu?: unknown };
		if (value.v !== VERSION || typeof value.savedAt !== "number") return null;
		const parsed = staffMenuSchema.safeParse(value.menu);
		return parsed.success ? { menu: parsed.data, savedAt: value.savedAt } : null;
	} catch {
		return null;
	}
}

export type MenuDeviceCache = {
	/** Puts the stored menu into the cache, unless the server's is there already. Once per restaurant. */
	hydrate(): Promise<void>;
	/** Stops writing. */
	stop(): void;
};

/**
 * Writes every successful `listMenu` answer (and every optimistic change to
 * it) to storage under the active restaurant, a little after it changes.
 */
export function createMenuDeviceCache(options: {
	storage: KeyValueStorage;
	queryClient: QueryClient;
	queryKey: readonly unknown[];
	restaurantId: () => string | null;
	delayMs?: number;
}): MenuDeviceCache {
	const { storage, queryClient, queryKey } = options;
	const hash = JSON.stringify(queryKey);
	const hydrated = new Set<string>();
	let timer: ReturnType<typeof setTimeout> | undefined;

	const write = () => {
		timer = undefined;
		const restaurantId = options.restaurantId();
		const state = queryClient.getQueryState<StaffMenu>(queryKey);
		if (!restaurantId || !state?.data) return;
		void storage
			.setItem(menuStorageKey(restaurantId), encodeMenu(state.data, state.dataUpdatedAt))
			.catch(() => {});
	};

	const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
		if (event.type !== "updated" || JSON.stringify(event.query.queryKey) !== hash) return;
		// Fetches and optimistic edits alike: the device keeps what staff see.
		if (event.action.type !== "success") return;
		if (timer) clearTimeout(timer);
		timer = setTimeout(write, options.delayMs ?? 500);
	});

	return {
		async hydrate() {
			const restaurantId = options.restaurantId();
			if (!restaurantId || hydrated.has(restaurantId)) return;
			hydrated.add(restaurantId);
			const stored = decodeMenu(
				await storage.getItem(menuStorageKey(restaurantId)).catch(() => null),
			);
			// A restaurant switch or the server's answer while reading wins.
			if (!stored || options.restaurantId() !== restaurantId) return;
			if (queryClient.getQueryData(queryKey) !== undefined) return;
			queryClient.setQueryData(queryKey, stored.menu, { updatedAt: stored.savedAt });
		},
		stop() {
			unsubscribe();
			if (timer) clearTimeout(timer);
		},
	};
}
